import {
  Box, Typography, Button, TextField, MenuItem, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Activity, FileText, Sparkles, ExternalLink, FlaskConical } from 'lucide-react';
import { AccordionSection } from '../common/AccordionSection';
import { EmptyState } from '../common/EmptyState';
import { Visit } from '../../types';
import { formatDate } from '../../lib/utils';
import { PatientLabOrdersSection } from './PatientLabOrdersSection';
import { useTranslation } from 'react-i18next';

interface PatientDiagnosticsSectionProps {
  expanded: string | false;
  onExpandChange: (panel: string) => void;
  canRecordReadings: boolean;
  onUploadClick: () => void;
  sortedVisits: Visit[];
  selectedVisitId: string;
  setSelectedVisitId: (id: string) => void;
  allLabDocuments: any[];
  analyzingLabId: string | null;
  handleAnalyzeLab: (doc: any) => void;
  handleViewDocument: (filePath: string) => void;
  patientId: string;
  isDark?: boolean;
}

export function PatientDiagnosticsSection({
  expanded,
  onExpandChange,
  canRecordReadings,
  onUploadClick,
  sortedVisits,
  selectedVisitId,
  setSelectedVisitId,
  allLabDocuments,
  analyzingLabId,
  handleAnalyzeLab,
  handleViewDocument,
  patientId,
}: PatientDiagnosticsSectionProps) {
  const { t } = useTranslation('patients');
  const theme = useTheme();

  return (
    <AccordionSection
      panelId="readings"
      expanded={expanded}
      onExpandChange={onExpandChange}
      icon={<Activity size={20} color={theme.palette.warning.main} />}
      title={t('diagnosticsAndTools')}
    >
      <Box>
        {canRecordReadings && (
          <Box sx={{ mb: 2 }}>
            <Button variant="contained" size="small" startIcon={<FileText size={16} />} onClick={onUploadClick}>
              {t('uploadDocument')}
            </Button>
          </Box>
        )}

        {/* Lab Orders — pending and completed with linked report access */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlaskConical size={16} /> {t('labOrders')}
        </Typography>
        <PatientLabOrdersSection patientId={patientId} />

        <Divider sx={{ my: 3 }} />

        {sortedVisits.length > 0 && (
          <Box sx={{ mb: 2, maxWidth: 320 }}>
            <TextField
              select fullWidth size="small" label={t('sourceVisitForReference')}
              value={selectedVisitId}
              onChange={(e) => setSelectedVisitId(e.target.value)}
              variant="standard"
            >
              {sortedVisits.map((visit) => (
                <MenuItem key={visit.id} value={visit.id}>
                  {formatDate(visit.visitDate)} -{' '}
                  {visit.doctorName ? `Dr. ${visit.doctorName}` : 'Consulting Doctor'}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileText size={16} /> {t('clinicalDocumentArchive')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('allHistoricalDocumentsUploadedViaThe')}
        </Typography>

        {allLabDocuments.length === 0 ? (
          <EmptyState
            icon={<FileText size={48} />}
            message="No uploaded documents found for this patient."
            subMessage="Uploaded files will appear here once attached to a prescription."
            dashed
          />
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('fileName')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('doctor')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('date')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t('action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allLabDocuments.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color={theme.palette.primary.main} />
                        <Typography variant="body2" fontWeight={500}>{doc.fileName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="body2">{doc.doctorName}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{formatDate(doc.prescriptionDate)}</Typography></TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          startIcon={analyzingLabId === doc.id ? <CircularProgress size={14} /> : <Sparkles size={14} />}
                          onClick={() => handleAnalyzeLab(doc)}
                          disabled={!!analyzingLabId}
                          variant="contained"
                          color="secondary"
                          sx={{
                            borderRadius: 4,
                            background: 'linear-gradient(45deg, #7b1fa2 30%, #4a148c 90%)',
                            color: '#fff',
                            whiteSpace: 'nowrap',
                            '&:hover': { background: 'linear-gradient(45deg, #6a1b9a 30%, #311b92 90%)', color: '#fff' },
                          }}
                        >
                          {t('aiAnalyze')}
                        </Button>
                        <Button
                          size="small"
                          startIcon={<ExternalLink size={14} />}
                          onClick={() => handleViewDocument(doc.filePath)}
                          variant="outlined"
                          sx={{ borderRadius: 4 }}
                        >
                          {t('view')}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </AccordionSection>
  );
}
